import { replyAwaitingInput } from "@/api/awaiting-input";
import {
  captureContextualDecisionSubmission,
  recordContextualTaskAcceptance,
} from "@/desktop/onboarding/contextual-task-acceptance";
import { resolveCursorQuestion } from "@/lib/resolve-cursor-question";
import { captureDesktopChatAuthScopeGuard } from "@/desktop/desktop-chat-auth-scope";
import { holdSendForGuide } from "@/desktop/onboarding/quick-guide/guide-send-hold";
import { dispatchUserMessage } from "@/hooks/use-dispatch-user-message";
import { nativeChildSubmitOptions } from "@/lib/input-bar-target-submit";
import { resolveWorkToastQuestion } from "@/lib/work-toast-item";
import { applyWorkToastQuestionDelivery } from "@/lib/work-toast-question-delivery";
import { withMutationEmitSuppressed } from "@/lib/mirrored-store";
import { getWindowRoleSync } from "@/desktop/use-window-role";
import { submitQuestionAttachments } from "@/lib/submit-question-attachments";
import {
  useWorkToastStore,
  type WorkToastItem,
} from "@/stores/work-toast-store";

export async function answerWorkToastQuestion(
  item: WorkToastItem,
  answer: string,
  selectedOption: string,
  imageIds: string[] = [],
): Promise<boolean> {
  const { question, projectId } = item;
  if (!question || !projectId) return false;
  const isCurrentScope = captureDesktopChatAuthScopeGuard();
  captureContextualDecisionSubmission(question.awaitingInputId);
  try {
    if (imageIds.length) {
      const sent = await submitQuestionAttachments({
        projectId,
        conversationId: item.conversationId,
        awaitingInputId: question.awaitingInputId,
        text: answer,
        imageIds,
        nativeChildInstruction: item.source === "native_worker",
      });
      if (!sent || !isCurrentScope()) return false;
      resolveCursorQuestion(question.awaitingInputId);
      resolveWorkToastQuestionLocally(question.awaitingInputId);
      return true;
    }
    const receipt = await replyAwaitingInput(
      projectId,
      question.awaitingInputId,
      { answer, selectedOption },
    );
    if (!isCurrentScope()) return false;
    if (receipt)
      recordContextualTaskAcceptance({
        status: receipt.status,
        project_id: projectId,
        execution_id: receipt.execution,
        conversation_id: receipt.conversation_id,
        accepted_task_id: receipt.accepted_task_id,
        saved_task_ids: receipt.saved_task_ids,
        selected_option: receipt.selected_option,
        answered_at: receipt.answered_at,
        answer_delivery_status: receipt.answer_delivery_status,
      });
    if (receipt?.answer_delivery_status) {
      const status = receipt.answer_delivery_status;
      applyReplyLocally(() => applyWorkToastQuestionDelivery(
        {
          awaiting_input_id: question.awaitingInputId,
          execution_id: receipt.execution,
          project_id: projectId,
          conversation_id: receipt.conversation_id ?? item.conversationId,
          task_id: receipt.task_id ?? item.taskId,
          native_subagent_id:
            receipt.native_subagent_id ?? item.nativeSubagentId,
          status,
          error: receipt.answer_delivery_error,
        },
        { workToastStore: useWorkToastStore },
        Date.now(),
      ));
    }
  } catch {
    // replyAwaitingInput already reported it (409 resolves as success).
    return false;
  }
  if (!isCurrentScope()) return false;
  resolveCursorQuestion(question.awaitingInputId);
  // The resolve broadcast can be lost (reconnect, other device), so apply it here too.
  resolveWorkToastQuestionLocally(question.awaitingInputId);
  return true;
}

/** The reply response is server data. A mirror window applies it locally,
 *  like a frame: sending its whole `items` map to the owner would roll back a
 *  delivery the owner already applied (`work-toast-store.ts`). The owner's
 *  copy is canonical and still reaches the mirrors. */
function applyReplyLocally(apply: () => void): void {
  if (getWindowRoleSync() === "owner") apply();
  else withMutationEmitSuppressed(apply);
}

function resolveWorkToastQuestionLocally(awaitingInputId: string): void {
  applyReplyLocally(() =>
    resolveWorkToastQuestion({ workToastStore: useWorkToastStore }, awaitingInputId, Date.now()),
  );
}

export async function sendWorkToastFollowUp(
  item: WorkToastItem,
  text: string,
  imageIds: string[] = [],
): Promise<boolean> {
  const { conversationId } = item;
  if (!conversationId) return false;
  if (holdSendForGuide("typed")) return false;
  let result: ReturnType<typeof dispatchUserMessage>;
  if (item.source === "native_worker") {
    const { textOnly, ...native } = nativeChildSubmitOptions(conversationId);
    result = dispatchUserMessage({
      ...native,
      literalText: textOnly,
      text,
      imageIds,
      position: { x: 0, y: 0 },
    });
  } else {
    result = dispatchUserMessage({
      text,
      imageIds,
      position: { x: 0, y: 0 },
      conversationId,
      startNewConversation: false,
      keepConversation: true,
      ignoreInputBarSessionState: true,
      composerId: `work-toast:${item.key}`,
    });
  }
  if (!result.sent) return false;
  return result.accepted;
}
